import { motion } from "framer-motion";
import { ShieldCheck, Star, BadgeCheck, HandCoins } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { publicApi } from "@/lib/api";
import { INTRO } from "@/lib/brand";

const HERO_BG = "/hero-branches.jpg";

const TRUST = [
  { icon: ShieldCheck, label: "Veteran Founded" },
  { icon: Star, label: "5-Star Reviews" },
  { icon: HandCoins, label: "No Upfront Cost" },
  { icon: BadgeCheck, label: "Success-Based" },
];

/**
 * Computes how long the hero should wait before revealing itself.
 * Synced with BirdFlightIntro: hero begins emerging exactly when the bird
 * is in its final approach, creating a single continuous reveal.
 */
function useHeroIntroDelay() {
  return useMemo(() => {
    if (typeof window === "undefined") return 0;
    if (!INTRO.enabled) return 0;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return 0;
    if (INTRO.replayMode === "session-once") {
      try { if (sessionStorage.getItem("ftsBirdIntroPlayed")) return 0; } catch (e) { /* noop */ }
    }
    const isMobile = window.innerWidth < 768;
    const dur = isMobile ? INTRO.mobileDurationSec : INTRO.desktopDurationSec;
    return Math.max(0, dur - INTRO.heroSyncOffsetSec);
  }, []);
}

export default function Hero() {
  const [cfg, setCfg] = useState({
    headline: "Get the Benefits You Earned Through Your Service",
    subheadline: "We help Veterans navigate the VA disability claims process with no upfront cost. You don't pay unless we're successful.",
  });
  const introDelay = useHeroIntroDelay();

  useEffect(() => {
    publicApi.get("/public/settings").then(({ data }) => {
      if (data?.headline) setCfg({ headline: data.headline, subheadline: data.subheadline });
    }).catch(() => {});
  }, []);

  return (
    <section id="top" className="relative pt-32 pb-24 md:pt-44 md:pb-36 overflow-hidden">
      {/* Soft halo background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F6FB] via-white to-white" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-40"
          style={{ background: "radial-gradient(closest-side, #cfe0fa, transparent 70%)" }} />
        <div className="absolute inset-0 bg-grain opacity-60" />
      </div>

      <div className="max-w-6xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: introDelay }}
          className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-500 mb-6"
          data-testid="hero-eyebrow"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#C8102E]" /> A mission for those who served
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: introDelay + 0.08, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tighter text-[#0A1930] leading-[1.02]"
          data-testid="hero-headline"
        >
          {cfg.headline}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: introDelay + 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subheadline"
        >
          {cfg.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: introDelay + 0.36, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center"
        >
          <a href="#booking"
            className="group bg-[#0066CC] text-white rounded-full px-7 py-4 text-[15px] font-medium hover:bg-[#0055AA] transition-all active:scale-95 shadow-[0_10px_30px_-12px_rgba(0,102,204,0.55)]"
            data-testid="hero-cta-primary">
            Schedule Your Free 15-Minute Consultation
          </a>
          <a href="#difference"
            className="text-[#0A1930] underline-offset-4 hover:underline rounded-full px-6 py-4 text-[15px] font-medium"
            data-testid="hero-cta-secondary">
            See how we help Veterans →
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.0, delay: introDelay + 0.55 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-3xl mx-auto"
          data-testid="trust-row"
        >
          {TRUST.map(({ icon: I, label }) => (
            <div key={label} className="flex items-center gap-2 justify-center md:justify-start text-[13px] text-slate-600">
              <I className="w-4 h-4 text-[#0066CC]" strokeWidth={1.75} />
              <span>{label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Decorative image strip */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: introDelay + 0.65 }}
        className="mt-20 md:mt-28 max-w-6xl mx-auto px-6"
      >
        <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_80px_-40px_rgba(10,25,48,0.4)]">
          <img src={HERO_BG} alt="" className="w-full h-[360px] md:h-[480px] object-cover" />
          {/* Cinematic bottom scrim — keeps caption legible over any imagery */}
          <div className="absolute inset-x-0 bottom-0 h-[60%] md:h-[55%] bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
          <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-auto md:max-w-md">
            <p className="text-[11px] tracking-[0.22em] uppercase text-white/85" data-testid="hero-strip-eyebrow">
              Built by Veterans, for Veterans
            </p>
            <p
              className="font-display text-2xl md:text-3xl text-white mt-2 leading-tight"
              style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
              data-testid="hero-strip-headline"
            >
              You served. Now let us serve you.
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
